import { forwardRef } from "react";
import * as ThemeUI from "theme-ui";

import { lightTheme } from "../../themes/ms5/theme";
import * as buttonVariants from "../../themes/ms5/variants/buttons";
import * as imageVariants from "../../themes/ms5/variants/images";
import * as layoutVariants from "../../themes/ms5/variants/layout";
import * as linkVariants from "../../themes/ms5/variants/links";
import * as textVariants from "../../themes/ms5/variants/text";

export type ThemeUIStyleObject = ThemeUI.ThemeUIStyleObject;

export type Theme = typeof lightTheme;

/**
 * Get all the nested keys of a theme object.
 *
 * This is useful for getting the keys of a theme object to use with getThemeValue.
 * it will return a string with the nested keys separated by a dot.
 * if a value is an array or string it will stop being recursive.
 */
type NestedKeys<T> = {
  [K in keyof T]: K extends string
    ? T[K] extends any[]
      ? `${K}`
      : T[K] extends object
      ? `${K}` | `${K}.${NestedKeys<T[K]>}`
      : `${K}`
    : never;
}[keyof T];

export type ThemeKeys = NestedKeys<Theme>;

/**
 * Get Theme Value
 *
 * This is a wrapper around the theme-ui get function that allows us to use our typed theme.
 */
export const getThemeValue = ThemeUI.get as (
  themeObject: object,
  path: ThemeKeys,
  fallback?: unknown,
  p?: number,
  undef?: unknown,
) => any;

// Overriding Theme UI types since our theme values are typed.
interface ThemeUIContext extends Omit<ThemeUI.ThemeUIContextValue, "theme"> {
  theme: Theme;
}

export const useThemeUI = ThemeUI.useThemeUI as () => ThemeUIContext;

export const jsx = ThemeUI.jsx;
export const ThemeUIProvider = ThemeUI.ThemeUIProvider;

export const Box = ThemeUI.Box;
export const Grid = ThemeUI.Grid;
export const Flex = ThemeUI.Flex;

export const Close = ThemeUI.Close;
export const Divider = ThemeUI.Divider;
export const Donut = ThemeUI.Donut;
// export const Alert = ThemeUI.Alert;
export const Spinner = ThemeUI.Spinner;

export const AspectRatio = ThemeUI.AspectRatio;
export const AspectImage = ThemeUI.AspectImage;

export const Input = ThemeUI.Input;
export const Label = ThemeUI.Label;

export const Textarea = ThemeUI.Textarea;
export const Select = ThemeUI.Select;

/**
 * Theme Ui  Link component.
 *
 * Link style variants can be defined in the theme/variants/links.ts.
 * @see https://theme-ui.com/components/link
 */
export interface LinkProps extends ThemeUI.LinkProps {
  /** Variants pulled directly from the link variants in our theme */
  variant?: keyof typeof linkVariants;
}
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  return <ThemeUI.Link {...props} ref={ref} />;
});

Link.displayName = "Link";

/**
 * Theme Ui button component.
 *
 * Button style variants can be defined in the theme/variants/buttons.ts.
 * @see https://theme-ui.com/components/button
 */
export interface ButtonProps extends ThemeUI.ButtonProps {
  /** Variants pulled directly from the button variants in our theme */
  variant?: keyof typeof buttonVariants;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <ThemeUI.Button {...props} ref={ref} />;
});

Button.displayName = "Button";

/**
 * Theme Ui  icon button component.
 *
 * Button style variants can be defined in the theme/variants/buttons.ts.
 * @see https://theme-ui.com/components/icon-button
 */
export interface IconButtonProps extends ThemeUI.IconButtonProps {
  /** Variants pulled directly from the button variants in our theme */
  variant?: keyof typeof buttonVariants;
}
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  return <ThemeUI.IconButton {...props} ref={ref} />;
});

IconButton.displayName = "IconButton";

/**
 * Theme Ui  layout component.
 *
 * Layout style variants can be defined in the theme/variants/layout.ts.
 * @see https://theme-ui.com/components/container
 */
export interface ContainerProps extends ThemeUI.ContainerProps {
  /** Variants pulled directly from the layout variants in our theme */
  variant?: keyof typeof layoutVariants;
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
  return <ThemeUI.Container {...props} ref={ref} />;
});

Container.displayName = "Container";

/**
 * Theme Ui  typographic component.
 *
 * Heading style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/heading
 */
export interface HeadingProps extends ThemeUI.HeadingProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>((props, ref) => {
  return <ThemeUI.Heading {...props} ref={ref} />;
});

Heading.displayName = "Heading";

/**
 * Theme Ui  typographic component.
 *
 * Text style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/text
 */
export interface TextProps extends ThemeUI.TextProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Text = forwardRef<HTMLDivElement, TextProps>((props, ref) => {
  return <ThemeUI.Text as={props.as ?? "span"} {...props} ref={ref} />;
});

Text.displayName = "Text";

/**
 * Theme Ui  typographic component.
 *
 * Text style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/text
 */
export interface ParagraphProps extends ThemeUI.TextProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>((props, ref) => {
  return <ThemeUI.Text as={props.as ?? "p"} {...props} ref={ref} />;
});

Paragraph.displayName = "Paragraph";

/**
 * Theme Ui  Image component.
 *
 * Updated to default to loading="lazy"
 *
 * @see https://theme-ui.com/components/image
 */
export interface ImageProps extends ThemeUI.ImageProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof imageVariants;
}
export const Image = forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
  return <ThemeUI.Image loading={props.loading ?? "lazy"} {...props} ref={ref} />;
});

Image.displayName = "Image";
